// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 * Single navigation arrow for the `XngCarousel`.
 *
 * Reads the carousel from {@link CarouselContext}, picks the appropriate icon
 * for the current direction (up/down for vertical, chevrons for horizontal),
 * and wires up click + disabled state via {@link useArrowElement}.
 */

import translate from "@insite/client-framework/Translate";
import { ButtonIcon } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import { IconWrapper } from "@insite/mobius/Icon";
import ArrowDown from "@insite/mobius/Icons/ArrowDown";
import ArrowUp from "@insite/mobius/Icons/ArrowUp";
import { getProp } from "@insite/mobius/utilities";
import { useCarouselContext } from "@vanmeter/App/Components/XngCarousel/CarouselContext";
import { useArrowElement } from "@vanmeter/App/Components/XngCarousel/hooks";
import { ArrowPosition, NavigationProps } from "@vanmeter/App/Components/XngCarousel/models";
import { getArrowPosition } from "@vanmeter/App/Components/XngCarousel/utils";
import SmallArrowLeft from "@vanmeter/XDL/Icons/IconPaginationArrowLeft";
import SmallArrowRight from "@vanmeter/XDL/Icons/IconPaginationArrowRight";
import IconPaginationCarouselArrowLeft from "@vanmeter/XDL/Icons/IconPaginationCarouselArrowLeft";
import IconPaginationCarouselArrowRight from "@vanmeter/XDL/Icons/IconPaginationCarouselArrowRight";
import { color, xClassNames, xScreen } from "@vanmeter/XDL/Utilities";
import React, { FC, useCallback } from "react";
import { css } from "styled-components";

// ─── Constants ────────────────────────────────────────────────────────────────

const ARROW_ICON_SIZE_INLINE = 18;
const ARROW_ICON_SIZE_OUTER = 56;

const getArrowIcons = (outside: boolean): Record<ArrowPosition, { src: any; size: number }> => ({
    top: { src: ArrowUp, size: ARROW_ICON_SIZE_INLINE },
    bottom: { src: ArrowDown, size: ARROW_ICON_SIZE_INLINE },
    left: {
        src: outside ? IconPaginationCarouselArrowLeft : SmallArrowLeft,
        size: outside ? ARROW_ICON_SIZE_OUTER : ARROW_ICON_SIZE_INLINE,
    },
    right: {
        src: outside ? IconPaginationCarouselArrowRight : SmallArrowRight,
        size: outside ? ARROW_ICON_SIZE_OUTER : ARROW_ICON_SIZE_INLINE,
    },
});

// ─── Style ────────────────────────────────────────────────────────────────────

/** Build the inline CSS passed to the `Clickable` for a given arrow position. */
const generateArrowStyle = (arrowPosition: ArrowPosition, navigationOutSide?: boolean) => ({
    size: !navigationOutSide ? ARROW_ICON_SIZE_INLINE : ARROW_ICON_SIZE_OUTER,
    css: css`
        position: static;
        margin-${arrowPosition === "left" ? "right" : "left"}: 20px;
        background: transparent;
        transform: none;
        z-index: 10;
        padding: 0;
        border: 0;
        border-radius: 50%;
        display: flex;
        justify-content: center;
        align-items: center;
        outline: none !important;
        cursor: pointer;
        &&& > ${IconWrapper} {
            transition: all ${getProp("theme.transition.duration.regular", "300")}ms ease-in-out;

            ${
                !navigationOutSide
                    ? css`
                          color: ${color("common.white")};
                          &:hover {
                              color: ${color("common.ui17")};
                          }
                      `
                    : css`
                          color: ${color("secondary")};
                          box-shadow: rgba(12, 9, 8, 0.5) 0px 0px 4px 0px;
                          border-radius: 50%;
                          path {
                              fill: ${color("primary")};
                          }
                          &:hover {
                              color: ${color("common.ui17")};
                          }
                          &:focus {
                              color: ${color("common.ui8")};
                          }
                          &,
                          svg {
                              width: ${ARROW_ICON_SIZE_OUTER}px;
                              height: ${ARROW_ICON_SIZE_OUTER}px;
                              ${xScreen("lte", "md")} {
                                  width: 40px;
                                  height: 40px;
                              }
                          }
                      `
            }
        }

        &:focus {
            box-shadow: none;
        }

        ${
            (arrowPosition === "top" || arrowPosition === "bottom") &&
            css`
                position: relative;
                inset: 0;
                transform: none;
                margin: auto;
            `
        }
        ${
            (arrowPosition === "left" || arrowPosition === "right") &&
            navigationOutSide &&
            css`
                position: absolute;
                top: 50%;
                transform: translateY(-50%);
                margin: 0;
                ${arrowPosition}: 0;
            `
        }

        &.carousel-button-disabled {
            visibility: hidden;
        }

        ${xScreen("lte", "md")} {
           &.arrow-on-desktop {
                display: none;
            }
        }
    `,
});

// ─── Component ────────────────────────────────────────────────────────────────

const ArrowNavigation: FC<NavigationProps> = React.memo(
    ({ arrowType, showOnMobile = true, buttonIconProps, navigationOutside, ...otherProps }) => {
        const ctx = useCarouselContext();
        const carouselApi = ctx?.carouselApi ?? null;
        const direction = ctx?.direction ?? "horizontal";

        const arrowPosition = getArrowPosition(arrowType, direction);
        const { src: iconSrc, size: iconSize } = getArrowIcons(navigationOutside ?? false)[arrowPosition];

        const { className, disabled } = useArrowElement({
            carousel: carouselApi,
            showOnMobile,
            arrowType,
        });

        const onClick = useCallback(
            (event: React.MouseEvent<HTMLButtonElement>) => {
                if (!carouselApi || disabled) return;

                if (arrowType === "next") carouselApi.scrollNext();
                else carouselApi.scrollPrev();

                // XNG-Changes: Reset the autoplay timer whenever next/prev is clicked.
                carouselApi.plugins().autoplay?.reset();

                otherProps.onClick?.(event);
            },
            [arrowType, carouselApi, disabled, otherProps.onClick],
        );

        if (!carouselApi) return null;

        return (
            <Clickable
                {...{ arrowPosition, showOnMobile }}
                className={xClassNames.append(className, otherProps.className)}
                {...generateArrowStyle(arrowPosition, navigationOutside)}
                aria-label={translate(arrowType === "next" ? "Next slide" : "Previous slide")}
                onClick={onClick}
                data-test-selector={`carousel-${arrowPosition}-arrow-button`}
            >
                <ButtonIcon
                    src={iconSrc}
                    size={iconSize}
                    color="common.white"
                    className={`carousel-${arrowPosition}-icon${disabled ? " disabled" : ""}`}
                    {...buttonIconProps}
                />
            </Clickable>
        );
    },
);

ArrowNavigation.displayName = "ArrowNavigation";

export default ArrowNavigation;
